/**
 * Global logging configuration for the WalkCompanion application
 * This file contains flags that control different types of logging across web and mobile
 */

export interface LoggingConfig {
    // Challenge-related logging
    challenges: {
        enabled: boolean;
        userData: boolean;
        eligibility: boolean;
        loading: boolean;
        progress: boolean;
    };

    // Authentication logging
    auth: {
        enabled: boolean;
        stateChanges: boolean;
        userProfile: boolean;
    };

    // General application logging
    general: {
        enabled: boolean;
        errors: boolean;
        warnings: boolean;
    };
}

// Default configuration - set to false to reduce console noise
export const defaultLoggingConfig: LoggingConfig = {
    challenges: {
        enabled: false, // Master switch for all challenge logging
        userData: false,
        eligibility: false,
        loading: false,
        progress: false,
    },
    auth: {
        enabled: true, // Keep auth logging enabled for debugging
        stateChanges: true,
        userProfile: true,
    },
    general: {
        enabled: true, // Keep general logging enabled
        errors: true,
        warnings: true,
    },
};

// Current configuration - can be modified at runtime
let currentConfig: LoggingConfig = { ...defaultLoggingConfig };

/**
 * Update the logging configuration
 */
export function updateLoggingConfig(newConfig: Partial<LoggingConfig>): void {
    currentConfig = { ...currentConfig, ...newConfig };
}

/**
 * Get the current logging configuration
 */
export function getLoggingConfig(): LoggingConfig {
    return currentConfig;
}

/**
 * Check if challenge logging is enabled
 */
export function isChallengeLoggingEnabled(): boolean {
    return currentConfig.challenges.enabled;
}

/**
 * Check if specific challenge logging type is enabled
 */
export function isChallengeLoggingTypeEnabled(type: keyof LoggingConfig['challenges']): boolean {
    return currentConfig.challenges.enabled && currentConfig.challenges[type];
}

/**
 * Check if auth logging is enabled
 */
export function isAuthLoggingEnabled(): boolean {
    return currentConfig.auth.enabled;
}

/**
 * Check if specific auth logging type is enabled
 */
export function isAuthLoggingTypeEnabled(type: keyof LoggingConfig['auth']): boolean {
    return currentConfig.auth.enabled && currentConfig.auth[type];
}

/**
 * Check if general logging is enabled
 */
export function isGeneralLoggingEnabled(): boolean {
    return currentConfig.general.enabled;
}

/**
 * Check if specific general logging type is enabled
 */
export function isGeneralLoggingTypeEnabled(type: keyof LoggingConfig['general']): boolean {
    return currentConfig.general.enabled && currentConfig.general[type];
}

/**
 * Logger utility functions
 */
export const logger = {
    challenge: {
        log: (message: string, ...args: any[]) => {
            if (isChallengeLoggingEnabled()) {
                console.log(`🎯 [Challenge] ${message}`, ...args);
            }
        },
        userData: (message: string, ...args: any[]) => {
            if (isChallengeLoggingTypeEnabled('userData')) {
                console.log(`👤 [Challenge User] ${message}`, ...args);
            }
        },
        eligibility: (message: string, ...args: any[]) => {
            if (isChallengeLoggingTypeEnabled('eligibility')) {
                console.log(`✅ [Challenge Eligibility] ${message}`, ...args);
            }
        },
        loading: (message: string, ...args: any[]) => {
            if (isChallengeLoggingTypeEnabled('loading')) {
                console.log(`📋 [Challenge Loading] ${message}`, ...args);
            }
        },
        progress: (message: string, ...args: any[]) => {
            if (isChallengeLoggingTypeEnabled('progress')) {
                console.log(`📊 [Challenge Progress] ${message}`, ...args);
            }
        },
    },
    auth: {
        log: (message: string, ...args: any[]) => {
            if (isAuthLoggingEnabled()) {
                console.log(`🔐 [Auth] ${message}`, ...args);
            }
        },
        stateChanges: (message: string, ...args: any[]) => {
            if (isAuthLoggingTypeEnabled('stateChanges')) {
                console.log(`🔄 [Auth State] ${message}`, ...args);
            }
        },
        userProfile: (message: string, ...args: any[]) => {
            if (isAuthLoggingTypeEnabled('userProfile')) {
                console.log(`👤 [Auth Profile] ${message}`, ...args);
            }
        },
    },
    general: {
        log: (message: string, ...args: any[]) => {
            if (isGeneralLoggingEnabled()) {
                console.log(`ℹ️ [General] ${message}`, ...args);
            }
        },
        error: (message: string, ...args: any[]) => {
            if (isGeneralLoggingTypeEnabled('errors')) {
                console.error(`❌ [Error] ${message}`, ...args);
            }
        },
        warning: (message: string, ...args: any[]) => {
            if (isGeneralLoggingTypeEnabled('warnings')) {
                console.warn(`⚠️ [Warning] ${message}`, ...args);
            }
        },
    },
}; 